import { CommonModule, KeyValue } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, HostBinding, Input, OnChanges } from '@angular/core';
import { ReteModule } from 'rete-angular-plugin/15';
import { SocketSelection } from '../../../core/socket-selection';
import { SocketSelectionTypes } from '../../../core/socket-selection-type';
import { StepData } from '../../../models/step-data';
import { NodeEvent } from '../../models/node-event';
import { NodeEventTypes } from '../../models/node-event-types';
import { WorkflowStepNode } from '../../models/workflow-step-node';

/**
 * Composant utilisé pour le rendu d'un noeud correspondant à une étape de workflow.
 */
@Component({
    selector: 'app-workflow-step-node',
    imports: [ReteModule, CommonModule],
    templateUrl: './workflow-step-node.component.html',
    styleUrl: './workflow-step-node.component.scss',
    host: {
        'data-testid': 'node',
    },
})
export class WorkflowStepNodeComponent implements OnChanges {
    /**
     * Données du noeud.
     */
    private _data!: WorkflowStepNode;

    /**
     * Données de l'étape du workflow affiché par ce noeud.
     */
    public workflowStepData: StepData;

    /**
     * Spécifie les données du noeud.
     */
    @Input()
    set data(value: WorkflowStepNode) {
        this._data = value;
        this.retrieveWorkflowStepData();
    }

    /**
     * Obtient les données du noeud
     */
    get data(): WorkflowStepNode {
        return this._data;
    }

    /**
     * Emet des évènements génrérés par les sockets (Input, Output) du noeud et ses contrôles.
     */
    @Input() emit!: (data: any) => void;

    /**
     * Fonction a appeler lorsque le rendu du noeud est terminé (facultatif).
     */
    @Input() rendered!: () => void;

    // TODO : verifier si necessaire
    seed = 0;

    /**
     * Valeur indiquant l'état de selection précedant du noeud (true : selectionné, false : deselectionné).
     */
    previousSelectedState = undefined;

    /**
     * Obtient une valeur indiquant si le noeud est selectionné.
     * Associe cette valeur à la classe selected --> TODO : verifier si c'est toujours le cas.
     */
    @HostBinding('class.selected') get selected() {
        return this.data.selected;
    }

    /**
     * Constructeur.
     * @param cdr : Reference au detecteur de changement Angular.
     */
    constructor(
        private cdr: ChangeDetectorRef,
        private host: ElementRef
    ) {}

    ngOnChanges(): void {
        this.retrieveWorkflowStepData();

        this.cdr.detectChanges();
        requestAnimationFrame(() => this.rendered());
        // TODO : verifier si necessaire
        this.seed++; // force render sockets

        this.emitSelectionChange();
    }

    ngOnV;

    /**
     * Emet une valeur indiquant que l'état de selection du noeud a changé.
     */
    private emitSelectionChange() {
        // Si une fonction de callback est spécifié, execute la fonction
        if (this.data.callBack) {
            if (this.previousSelectedState !== this.data.selected) {
                if (this.data.selected === true) {
                    this.data.callBack(this.data.id, new NodeEvent(NodeEventTypes.Selected));
                    ((this.host.nativeElement as HTMLElement).querySelector('#node-container') as HTMLDivElement).focus();
                } else if (this.data.selected === false) {
                    this.data.callBack(this.data.id, new NodeEvent(NodeEventTypes.Unselected));
                }
                this.previousSelectedState = this.data.selected;
            }
        }
    }

    /**
     * Récupère les données de l'étape de workflow.
     */
    private retrieveWorkflowStepData() {
        if (this._data) {
            this.workflowStepData = this._data.workflowStepData;
        } else {
            this.workflowStepData = null;
        }
    }

    public selectInputSocket(socketCode: number) {
        console.log('selectInputSocket', socketCode, this._data.id);
        if (this.data.callBack) {
            this.data.callBack(this.data.id, new NodeEvent(NodeEventTypes.InputSocketSelected, new SocketSelection(SocketSelectionTypes.Input, this._data.id, Number(socketCode))));
        }
    }

    public selectOutputSocket(socketCode: number) {
        console.log('selectOutputSocket', socketCode, this._data.id);
        if (this.data.callBack) {
            let socket = this.data.outputs[socketCode];
            this.data.callBack(
                this.data.id,
                new NodeEvent(NodeEventTypes.OutputSocketSelected, new SocketSelection(SocketSelectionTypes.Output, this._data.id, Number(socketCode)))
            );
        }
    }

    /**
     * Fonction utilisée pour trier les noeuds par rapport à leurs indexs
     * @param a
     * @param b
     * @returns
     */
    sortByIndex<N extends object, T extends KeyValue<string, N & { index?: number }>>(a: T, b: T) {
        const ai = a.value.index || 0;
        const bi = b.value.index || 0;

        return ai - bi;
    }
}

function myDefinedAlert() {
    alert('Hello! I am an alert box!!');
}
